import { computed } from 'vue'
import { TASK_UPLOAD_STATE_MAP, useTaskUpload } from './use-task-upload.ts'
import type { TaskUpload, TaskUploadNode, TaskUploadStatus } from '../types'

function hasSomeWithStatus(items: TaskUploadNode[], status: TaskUploadStatus) {
  return items.some((i) => i.status === status)
}
function allHaveStatus(items: TaskUploadNode[], status: TaskUploadStatus) {
  return items.length > 0 && items.every((i) => i.status === status)
}

function evaluateTaskUploadStatus(taskUpload: TaskUpload): TaskUploadStatus {
  const items = (() => {
    const folders = taskUpload.folders ? Object.values(taskUpload.folders) : []
    const files = taskUpload.files || []
    return [...folders, ...files]
  })()

  if (hasSomeWithStatus(items, 'processing')) return 'processing'
  if (allHaveStatus(items, 'error')) return 'error'
  if (hasSomeWithStatus(items, 'error') || hasSomeWithStatus(items, 'warning'))
    return 'warning'
  if (allHaveStatus(items, 'completed')) return 'completed'
  // default: if only added items remain
  return 'added'
}

/** @public - exported for testing in use-task-uploads.spec.ts */
export function updateTaskUploadNodeStatus(taskUpload: TaskUpload) {
  taskUpload.status = evaluateTaskUploadStatus(taskUpload)
}

export function getTaskUploadFilesSize(taskUpload: TaskUpload) {
  let totalSize = 0

  if (taskUpload.files && taskUpload.files.length) {
    taskUpload.files.forEach((f) => {
      if (f.data?.file) totalSize += f.data.file.size
    })
  }

  return totalSize
}

export function getTaskUploadFilesSizeUploaded(taskUpload: TaskUpload) {
  let totalUploadedSize = 0

  if (taskUpload.files && taskUpload.files.length) {
    taskUpload.files.forEach((f) => {
      if (f.data?.uploadSize?.uploaded)
        totalUploadedSize += f.data.uploadSize.uploaded
    })
  }

  return totalUploadedSize
}

export function useTaskUploads(taskUploads: MaybeRefOrGetter<TaskUpload[]>) {
  const getTaskUploadById = (id: number) => toValue(taskUploads)[id]
  const getTaskUploadItems = (id: number): TaskUploadNode[] => {
    const taskUpload = getTaskUploadById(id)
    if (!taskUpload) return []
    const folders = taskUpload.folders ? Object.values(taskUpload.folders) : []
    const files = taskUpload.files || []
    return [...folders, ...files]
  }

  const updateTaskUploadItemStatus = (taskUploadId: number) => {
    const taskUpload = getTaskUploadById(taskUploadId)
    if (!taskUpload) return
    const items = getTaskUploadItems(taskUploadId)
    if (items.every((i) => i.status === 'completed'))
      taskUpload.status = 'completed'
    else if (items.some((i) => i.status === 'processing'))
      taskUpload.status = 'processing'
    else if (items.some((i) => i.status === 'error'))
      taskUpload.status = 'error'
    else taskUpload.status = 'added'
  }

  const taskUploadsCount = computed(() => toValue(taskUploads).length)
  const taskUploadsAdded = computed(() =>
    toValue(taskUploads).filter((p) => p.status === 'added'),
  )
  const taskUploadsCountAdded = computed(() => taskUploadsAdded.value.length)
  const taskUploadsCountCompleted = computed(
    () => toValue(taskUploads).filter((p) => p.status === 'completed').length,
  )
  const taskUploadsCountError = computed(
    () => toValue(taskUploads).filter((p) => p.status === 'error').length,
  )
  const taskUploadsCountProcessing = computed(
    () => toValue(taskUploads).filter((p) => p.status === 'processing').length,
  )
  const isSomeTaskUploadItemProcessing = computed(() =>
    toValue(taskUploads).some((t) => t.status === 'processing'),
  )
  const taskUploadsCountWarning = computed(
    () => toValue(taskUploads).filter((p) => p.status === 'warning').length,
  )

  const taskUploadTotalSizeUploaded = computed(() => {
    let totalUploadedSize = 0
    toValue(taskUploads).forEach((taskUpload) => {
      totalUploadedSize += getTaskUploadFilesSizeUploaded(taskUpload)
    })
    return totalUploadedSize
  })

  const taskUploadsTotalSize = computed(() => {
    let totalSize = 0
    toValue(taskUploads).forEach((taskUpload) => {
      totalSize += getTaskUploadFilesSize(taskUpload)
    })
    return totalSize
  })

  const taskUploadsAllCompleted = computed(() => {
    if (taskUploadsCount.value === 0) return undefined
    return taskUploadsCountCompleted.value === taskUploadsCount.value
  })

  const taskUploadsAllDone = computed(() => {
    if (taskUploadsCount.value === 0) return undefined
    return (
      taskUploadsCountAdded.value === 0 &&
      taskUploadsCountProcessing.value === 0
    )
  })

  const taskUploadsNotDoneYetCount = computed(() => {
    if (taskUploadsCount.value === 0) return 0
    return toValue(taskUploads).filter(
      (p) => p.status === 'added' || p.status === 'processing',
    ).length
  })

  function getTaskUploadsByStatus(status: TaskUploadStatus | string) {
    return toValue(taskUploads).filter((p) => p.status === status)
  }

  const taskUploadsStateColor = computed(() => {
    if (taskUploadsCountProcessing.value > 0) {
      return TASK_UPLOAD_STATE_MAP['processing'].color
    }
    if (taskUploadsCountCompleted.value === taskUploadsCount.value) {
      return TASK_UPLOAD_STATE_MAP['completed'].color
    }

    if (taskUploadsCountWarning.value > 0) {
      return TASK_UPLOAD_STATE_MAP['warning'].color
    }

    if (taskUploadsCountError.value > 0) {
      return TASK_UPLOAD_STATE_MAP['error'].color
    }

    return undefined
  })

  const taskUploadsStateIcon = computed(() => {
    if (taskUploadsCountProcessing.value > 0) {
      return TASK_UPLOAD_STATE_MAP['processing'].icon
    }
    if (taskUploadsCountCompleted.value === taskUploadsCount.value) {
      return TASK_UPLOAD_STATE_MAP['completed'].icon
    }

    if (taskUploadsCountWarning.value > 0) {
      return TASK_UPLOAD_STATE_MAP['warning'].icon
    }

    if (taskUploadsCountError.value > 0) {
      return TASK_UPLOAD_STATE_MAP['error'].icon
    }

    return undefined
  })

  const { onTaskUploadItemSetStatus } = useTaskUpload()

  function onTaskUploadsSetTaskUploadStatus(
    taskUploadStatus: TaskUploadStatus,
  ) {
    toValue(taskUploads).forEach((taskUpload) => {
      onTaskUploadItemSetStatus(taskUploadStatus, taskUpload)
    })
  }

  function setTaskUploadNodeFile(
    taskUploadId: number,
    fileIndex: number,
    values: Partial<TaskUploadNode>,
  ) {
    const taskUpload = getTaskUploadById(taskUploadId)
    if (!taskUpload || !taskUpload.files) return
    const file = taskUpload.files[fileIndex]
    taskUpload.files[fileIndex] = {
      ...file,
      ...values,
      id: values.id ?? file.id,
      status: values.status ?? file.status,
      progress: values.progress ?? file.progress,
      data: { ...file.data, ...values.data },
    }
    // re-evaluate status
    updateTaskUploadItemStatus(taskUploadId)
  }

  function setTaskUploadFolderNode(
    taskUploadId: number,
    folderId: string,
    values: Partial<TaskUploadNode>,
  ) {
    const taskUpload = getTaskUploadById(taskUploadId)
    if (!taskUpload || !taskUpload.folders) return
    const folder = taskUpload.folders[folderId]
    taskUpload.folders[folderId] = {
      ...folder,
      ...values,
      id: values.id ?? folder.id,
      status: values.status ?? folder.status,
      progress: values.progress ?? folder.progress,
      data: { ...folder.data, ...values.data },
    }
    updateTaskUploadItemStatus(taskUploadId)
  }

  return {
    getTaskUploadById,
    getTaskUploadItems,
    updateTaskUploadItemStatus,
    taskUploadsCount,
    taskUploadsAdded,
    taskUploadsCountAdded,
    taskUploadsCountCompleted,
    taskUploadsCountError,
    taskUploadsCountProcessing,
    isSomeTaskUploadItemProcessing,
    taskUploadsCountWarning,
    taskUploadTotalSizeUploaded,
    taskUploadsTotalSize,
    taskUploadsAllCompleted,
    taskUploadsAllDone,
    taskUploadsNotDoneYetCount,
    getTaskUploadsByStatus,
    taskUploadsStateColor,
    taskUploadsStateIcon,
    onTaskUploadsSetTaskUploadStatus,
    setTaskUploadNodeFile,
    setTaskUploadFolderNode,
  }
}
